/* VALENCE — page behaviour: gentle reveals, quiet add-to-bag, atmosphere. */

const doc = document;
const root = doc.documentElement;
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const SOUND_KEY = 'valence:sound';

const store = {
  get(key) {
    try { return window.localStorage.getItem(key); } catch (_) { return null; }
  },
  set(key, value) {
    try { window.localStorage.setItem(key, value); } catch (_) { /* private mode: preference lasts this visit only */ }
  },
};

/* ---------- reveal ---------- */
function initReveal() {
  const items = doc.querySelectorAll('[data-v-reveal]:not(.is-in)');
  if (!items.length) return;
  if (reduceMotion.matches || !('IntersectionObserver' in window)) {
    items.forEach((el) => el.classList.add('is-in'));
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -6% 0px', threshold: 0.05 }
  );
  items.forEach((el) => io.observe(el));
}

/* ---------- add to bag without leaving the page ---------- */
function initBuyForms() {
  doc.querySelectorAll('form[data-v-buy]').forEach((form) => {
    if (form.dataset.vBound) return;
    form.dataset.vBound = '1';
    form.addEventListener('submit', async (event) => {
      if (!window.fetch) return;
      event.preventDefault();
      const status = doc.getElementById(form.dataset.vStatus);
      const formId = form.getAttribute('id');
      const buttons = formId
        ? doc.querySelectorAll(`[form="${formId}"], #${CSS.escape(formId)} [type="submit"]`)
        : form.querySelectorAll('[type="submit"]');
      buttons.forEach((b) => b.setAttribute('aria-busy', 'true'));
      try {
        const routes = (window.Theme && window.Theme.routes) || {};
        const res = await fetch(routes.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || 'Could not add to bag.');
        const cart = await fetch((routes.cart_url || '/cart') + '.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        const icon = doc.querySelector('cart-icon');
        if (icon && typeof icon.renderCartBubble === 'function') icon.renderCartBubble(cart.item_count);
        if (status) {
          status.innerHTML = `Added to your bag. <a class="v-link" href="${routes.cart_url || '/cart'}">View bag</a>`;
        }
      } catch (error) {
        if (status) status.textContent = error.message;
      } finally {
        buttons.forEach((b) => b.removeAttribute('aria-busy'));
      }
    });
  });
}

/* ---------- sticky purchase bar ---------- */
function initBar() {
  const bar = doc.querySelector('[data-vf-bar]');
  const anchor = doc.querySelector('[data-vf-buy-anchor]');
  const hero = doc.querySelector('[data-vf-hero]');
  if (!bar || !anchor || !('IntersectionObserver' in window)) return;
  let buyVisible = true;
  let heroVisible = true;
  const update = () => {
    const show = !buyVisible && !heroVisible;
    bar.classList.toggle('is-on', show);
    bar.toggleAttribute('inert', !show);
    root.style.setProperty('--vf-bar-h', show ? `${bar.offsetHeight}px` : '0px');
  };
  new IntersectionObserver(([e]) => { buyVisible = e.isIntersecting; update(); }).observe(anchor);
  const sound = doc.querySelector('[data-vf-sound]');
  if (hero) {
    new IntersectionObserver(([e]) => {
      heroVisible = e.isIntersecting;
      if (sound) sound.classList.toggle('is-compact', !heroVisible);
      update();
    }, { threshold: 0.15 }).observe(hero);
  }
  update();
}

/* ---------- atmosphere / sound ---------- */
function initSound() {
  const control = doc.querySelector('[data-vf-sound]');
  if (!control) return;
  const button = control.querySelector('button');
  const label = control.querySelector('[data-label]');
  const preset = control.dataset.preset || 'quiet';
  const engineUrl = control.dataset.engine;
  const src = control.dataset.src || '';
  let engine = null;
  let on = false;
  let pendingResume = null;

  const render = (state) => {
    control.classList.toggle('is-on', state === 'on');
    button.setAttribute('aria-pressed', state === 'on' ? 'true' : 'false');
    label.textContent = state === 'on' ? 'Mute sound' : state === 'resume' ? 'Resume sound' : 'Enable sound';
    button.title = label.textContent;
  };

  const start = async () => {
    try {
      if (!engine) {
        const mod = await import(engineUrl);
        engine = new mod.Atmosphere();
        engine.build(preset, { src });
      }
      await engine.start();
      on = true;
      render('on');
    } catch (error) {
      console.warn('[valence] sound unavailable', error);
      label.textContent = 'Sound unavailable';
    }
  };

  const stop = () => {
    on = false;
    if (engine) engine.stop();
    render('off');
  };

  const clearPending = () => {
    if (!pendingResume) return;
    ['pointerdown', 'keydown', 'touchstart'].forEach((t) => window.removeEventListener(t, pendingResume, true));
    pendingResume = null;
  };

  button.addEventListener('click', (event) => {
    event.stopPropagation();
    clearPending();
    if (on) {
      store.set(SOUND_KEY, 'off');
      stop();
    } else {
      store.set(SOUND_KEY, 'on');
      start();
    }
  });

  doc.addEventListener('visibilitychange', () => {
    if (!engine || !on) return;
    if (doc.hidden) engine.pause();
    else engine.start();
  });

  // Remembered preference. Browsers only allow sound after a gesture, so a
  // returning listener's choice resumes on their first tap or key press.
  if (store.get(SOUND_KEY) === 'on') {
    render('resume');
    pendingResume = (event) => {
      if (control.contains(event.target)) return;
      clearPending();
      start();
    };
    ['pointerdown', 'keydown', 'touchstart'].forEach((t) => window.addEventListener(t, pendingResume, true));
  } else {
    render('off');
  }
}

function init() {
  initReveal();
  initBuyForms();
  initBar();
  initSound();
}

if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init);
else init();

doc.addEventListener('shopify:section:load', () => { initReveal(); initBuyForms(); });
